import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { useFormik } from 'formik';
import { useNavigate } from 'react-router-dom';
import get from 'lodash/get';
import set from 'lodash/set';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { togglePaymentProcessing } from '../../../actions/payments';
import * as api from '../../../actions/api';
import * as router from '../../../constants/router';
import * as formConstants from '../../../constants/form';
import * as regex from '../../../constants/regex';
import invoiceDataSelector from '../../../selectors/invoice';
import paymentsSelector from '../../../selectors/payments';
import validatePayment from '../../../utils/validatePayment';
import validateForm from '../../../utils/validateForm';
import PaymentInput from '../PaymentInput';
import useStyles from '../../../utils/useStyles';
import { InvoiceContext } from '../../context/invoiceData';
import { useSurchargeCalculation } from '../../../hooks/usePaymentSubmit';

const ACHPayment = (props) => {
  const {
    invoiceID,
    currentPaymentAmount,
    setCurrentPaymentAmount,
  } = props;

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const selectorData = useSelector(invoiceDataSelector);
  const { submitting } = useSelector(paymentsSelector);

  const {
    paymentAgreementACH: paymentAgreement,
    allowPartialPayment,
    allowOverPayment,
    invoiceItems,
    dueDate,
    amountDue,
    maxPaymentAmount,
    minPaymentAmount,
    remainingBalance,
    surchargeFee,
    surchargeRate,
    surchargeMax,
    surchargeMin,
    surchargeLabel,
    surchargeAddition,
    achWebTerminal,
    contactFirstName,
    contactLastName,
    pastPayments,
    createdDate,
  } = selectorData;

  const requiredFields = achWebTerminal.required || [];
  const requiredMap = {};
  for (let i = 0; i < requiredFields.length; i += 1) {
    requiredMap[requiredFields[i]] = true;
  }

  const accountTypeRequired = requiredMap.account_type === true;
  const routingNumberRequired = requiredMap.routing === true;
  const accountNumberRequired = requiredMap.account_number === true;
  const accountOwnerNameRequired = requiredMap.account_holder_name === true;
  const billingStreetRequired = requiredMap.billing_street === true;
  const billingCityRequired = requiredMap.billing_city === true;
  const billingStateRequired = requiredMap.billing_state === true;
  const billingZipRequired = requiredMap.billing_zip === true;
  const driversLicenseRequired = requiredMap.driver_license === true;
  const identityVerificationRequired = requiredMap.identity_verification === true;

  const invoiceContext = useContext(InvoiceContext);
  const classes = useStyles();
  const [acceptedAgreement, toggleAcceptedAgreement] = useState(false);

  const {
    getSubtotal,
    getTransactionWithSurcharge,
    getSurchargeAmount,
  } = useSurchargeCalculation({
    allowPartialPayment,
    amountDue,
    surchargeMin,
    surchargeMax,
    productTransactionKey: 'ach_product_transaction',
  });

  const formik = useFormik({
    initialValues: {
      accountType: '',
      routingNumber: '',
      accountNumber: '',
      accountNumberConfirm: '',
      accountOwnerName: '',
      billingStreetAddress: '',
      billingCity: '',
      billingState: '',
      billingZip: '',
      driversLicenseNumber: '',
      driversLicenseState: '',
      dobYear: '',
      ssn4: '',
      paymentAmount: '',
      subtotal_amount: '',
      surcharge_amount: '',
    },
    validate: (values) => {
      const errors = {};
      const dobYearTrimmed = values.dobYear.trim();
      const paymentAmountFloat = parseFloat(values.paymentAmount);
      const remainingBalanceFloat = parseFloat(remainingBalance);
      const formFields = [
        {
          name: 'accountType',
          isRequired: accountTypeRequired,
          value: values.accountType,
          error: 'Account type is required.',
        },
        {
          name: 'routingNumber',
          isRequired: routingNumberRequired,
          value: values.routingNumber.trim(),
        },
        {
          name: 'accountNumber',
          isRequired: accountNumberRequired,
          value: values.accountNumber.trim(),
        },
        {
          name: 'accountNumberConfirm',
          isRequired: accountNumberRequired,
          value: values.accountNumberConfirm.trim(),
          valueMatch: values.accountNumber.trim(),
        },
        {
          name: 'accountOwnerName',
          isRequired: accountOwnerNameRequired,
          value: values.accountOwnerName.trim(),
          error: 'Account owner name is required.',
        },
        {
          name: 'billingStreetAddress',
          isRequired: billingStreetRequired,
          value: values.billingStreetAddress.trim(),
          error: 'Street address is required.',
        },
        {
          name: 'billingCity',
          isRequired: billingCityRequired,
          value: values.billingCity.trim(),
          error: 'City is required.',
        },
        {
          name: 'billingState',
          isRequired: billingStateRequired,
          value: values.billingState,
          error: 'State is required.',
        },
        {
          name: 'billingZip',
          isRequired: billingZipRequired,
          value: values.billingZip.trim(),
        },
        {
          name: 'driversLicenseNumber',
          isRequired: driversLicenseRequired,
          value: values.driversLicenseNumber.trim(),
        },
        {
          name: 'driversLicenseState',
          isRequired: driversLicenseRequired,
          value: values.driversLicenseState.trim(),
          error: 'DL state is required.',
        },
        {
          name: 'driversLicenseState',
          isRequired: driversLicenseRequired,
          value: values.driversLicenseState.trim(),
          error: 'DL state is required.',
        },
      ];

      for (let i = 0; i < formFields.length; i += 1) {
        const error = validateForm(formFields[i]);
        if (error !== false) {
          set(errors, formFields[i].name, error);
        }
      }

      if (values.ssn4.trim() === '' && dobYearTrimmed === '' && identityVerificationRequired) {
        set(errors, 'ssn4', 'Identity is required.');
        set(errors, 'dobYear', 'Identity is required.');
      }
      if (values.ssn4.trim() !== '' && dobYearTrimmed !== '' && identityVerificationRequired) {
        set(errors, 'ssn4', 'Enter SSN or DOB year.');
        set(errors, 'dobYear', 'Enter SSN or DOB year.');
      }
      if (values.ssn4.trim().length !== 4 && values.ssn4.trim() !== '') {
        set(errors, 'ssn4', 'Invalid Value');
      }
      if ((dobYearTrimmed.length !== 4 || parseInt(dobYearTrimmed, 10) > 2021) && dobYearTrimmed !== '') {
        set(errors, 'dobYear', 'Invalid Value');
      }

      // validate payment amount
      const paymentInfo = {
        amount: paymentAmountFloat,
        remaining: remainingBalanceFloat,
        max: maxPaymentAmount,
        min: minPaymentAmount,
        over: allowOverPayment,
        partial: allowPartialPayment,
      };
      const paymentValidationError = validatePayment(paymentInfo);
      if (paymentValidationError !== false) {
        set(errors, 'paymentAmount', paymentValidationError);
      }
      return errors;
    },
    onSubmit: (values) => {
      dispatch(togglePaymentProcessing(true));

      const payload = {
        account_type: values.accountType,
        account_number: values.accountNumber,
        routing: values.routingNumber,
        account_holder_name: values.accountOwnerName,
        billing_street: values.billingStreetAddress,
        billing_city: values.billingCity,
        billing_state: values.billingState,
        billing_zip: values.billingZip,
        dl_number: values.driversLicenseNumber,
        dl_state: values.driversLicenseState,
        dob_year: values.dobYear,
        ssn4: values.ssn4,
        payment_method: 'ach',
        terms_agree: '1',
        transaction_amount: getTransactionWithSurcharge(values.paymentAmount),
        subtotal_amount: getSubtotal(values.paymentAmount),
        surcharge_amount: getSurchargeAmount(values.paymentAmount),
      };
      const transactionRequest = [payload];
      api.submitPayment(invoiceID, payload).then((res) => {
        dispatch(togglePaymentProcessing(false));
        let errors = get(res, 'data.errors', null);
        const status = get(res, 'status', 0);
        const transactionInfo = get(res, 'data.quickinvoice.transactions', [])[0];
        const statusId = get(transactionInfo, 'status_id');
        if (errors === null && status === 201 && statusId === 301) {
          errors = { declined: 'Payment Declined' };
        }
        if (errors === null && status === 201 && statusId !== 301) {
          navigate(router.PAYMENT_STATUS_PAGE, {
            state: {
              invoiceNumber: get(res, 'data.quickinvoice.invoice_number'),
              invoiceTitle: get(res, 'data.quickinvoice.title', ''),
              remainingBalance: get(res, 'data.quickinvoice.remaining_balance', ''),
              dueDate: get(res, 'data.quickinvoice.due_date', null),
              transactionInfo,
              surchargeFee,
              surchargeRate,
              surchargeMax,
              surchargeMin,
              surchargeAddition,
              surchargeLabel,
              transactionRequest,
              lastFour: values.accountNumber.slice(values.accountNumber.length - 4),
              contactName: values.accountOwnerName || '',
              cardType: 'ach',
              success: true,
            },
          });
        } else {
          navigate(router.PAYMENT_STATUS_PAGE, {
            state: {
              invoiceNumber: get(res, 'data.quickinvoice.invoice_number'),
              invoiceTitle: get(res, 'data.quickinvoice.title', ''),
              transactionInfo: get(res, 'data.quickinvoice.transactions', [])[0],
              lastFour: values.accountNumber.slice(values.accountNumber.length - 4),
              contactName: values.accountOwnerName || '',
              cardType: 'ach',
              invoiceItems,
              pastPayments,
              surchargeFee,
              surchargeRate,
              surchargeMax,
              surchargeMin,
              surchargeAddition,
              surchargeLabel,
              createdDate,
              dueDate,
              amountDue,
              remainingBalance,
              success: false,
              errors,
            },
          });
        }
      }).catch(() => {
        dispatch(togglePaymentProcessing(false));
      });
    },
    validateOnChange: true,
    validateOnBlur: true,
  });

  useEffect(() => {
    formik.setFieldValue('accountType', '');
    formik.setFieldValue('billingState', '');

    invoiceContext.setACHFlag(true);

    if (contactFirstName && contactLastName) {
      formik.setFieldValue('accountOwnerName', `${contactFirstName} ${contactLastName}`);
    }
  }, [contactFirstName, contactLastName]);

  useEffect(() => {
    formik.setFieldValue('paymentAmount', currentPaymentAmount);
  }, [currentPaymentAmount]);

  return (
    <form noValidate autoComplete="off" onSubmit={formik.handleSubmit}>
      {accountTypeRequired && (
        <div className="ach-form-row">
          <FormControl variant="standard" sx={classes.formLabel} className="ach-form-row">
            <InputLabel required id="account-type-select-label">Bank Account Type</InputLabel>
            <Select
              style={{ width: '95%' }}
              label="account-type-select-label"
              id="account-select"
              name="accountType"
              value={formik.values.accountType}
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
            >
              <MenuItem value="checking">Checking</MenuItem>
              <MenuItem value="savings">Savings</MenuItem>
            </Select>
            {(formik.errors.accountType && formik.touched.accountType) ? <div className="input-error">{formik.errors.accountType}</div> : null}
          </FormControl>
        </div>
      )}
      {routingNumberRequired && (
        <div style={{ width: '100%', marginBottom: '20px' }}>
          <TextField
            variant="standard"
            sx={classes.formLabel}
            style={{ width: '95%' }}
            required
            id="routing-number"
            name="routingNumber"
            label="Routing Number"
            value={formik.values.routingNumber}
            onBlur={formik.handleBlur}
            onChange={(e) => {
              // eslint-disable-next-line no-restricted-globals
              if (e.target.value.trim() !== '' && isNaN(e.target.value)) {
                return;
              }
              formik.handleChange(e);
            }}
            inputProps={{ maxLength: 9 }}
          />
          {(formik.errors.routingNumber && formik.touched.routingNumber) ? <div className="input-error">{formik.errors.routingNumber}</div> : null}
        </div>
      )}
      {accountNumberRequired && (
        <div className="ach-form-row">
          <div className="ach-form-item-wrapper">
            <TextField
              variant="standard"
              sx={classes.formLabel}
              required
              className="ach-form-item"
              id="account-number"
              name="accountNumber"
              label="Account Number"
              value={formik.values.accountNumber}
              onBlur={formik.handleBlur}
              onChange={(e) => {
                // eslint-disable-next-line no-restricted-globals
                if (e.target.value.trim() !== '' && isNaN(e.target.value)) {
                  return;
                }
                formik.handleChange(e);
              }}
              inputProps={{ maxLength: 17 }}
            />
            {(formik.errors.accountNumber && formik.touched.accountNumber) ? <div className="input-error">{formik.errors.accountNumber}</div> : null}
          </div>
          <div className="ach-form-item-wrapper">
            <TextField
              variant="standard"
              sx={classes.formLabel}
              required
              className="ach-form-item"
              id="account-number-confirm"
              name="accountNumberConfirm"
              label="Re-Enter Account Number"
              value={formik.values.accountNumberConfirm}
              onPaste={(e) => e.preventDefault()}
              onBlur={formik.handleBlur}
              onChange={(e) => {
                // eslint-disable-next-line no-restricted-globals
                if (e.target.value.trim() !== '' && isNaN(e.target.value)) {
                  return;
                }
                formik.handleChange(e);
              }}
              inputProps={{ maxLength: 17 }}
            />
            {(formik.errors.accountNumberConfirm && formik.touched.accountNumberConfirm) ? <div className="input-error">{formik.errors.accountNumberConfirm}</div> : null}
          </div>
        </div>
      )}
      {accountOwnerNameRequired && (
        <div style={{ width: '100%', marginBottom: '20px' }}>
          <TextField
            variant="standard"
            sx={classes.formLabel}
            required
            style={{ width: '95%' }}
            id="account-owner-name"
            name="accountOwnerName"
            label="Account Owner Name"
            value={formik.values.accountOwnerName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            inputProps={{ maxLength: 60 }}
          />
          {(formik.errors.accountOwnerName && formik.touched.accountOwnerName) ? <div className="input-error">{formik.errors.accountOwnerName}</div> : null}
        </div>
      )}
      {billingStreetRequired && (
        <div style={{ width: '100%', marginBottom: '20px' }}>
          <TextField
            variant="standard"
            sx={classes.formLabel}
            required
            style={{ width: '95%' }}
            id="billing-street-address"
            name="billingStreetAddress"
            label="Billing Address"
            value={formik.values.billingStreetAddress}
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
          />
          {(formik.errors.billingStreetAddress && formik.touched.billingStreetAddress) ? <div className="input-error">{formik.errors.billingStreetAddress}</div> : null}
        </div>
      )}
      {(billingCityRequired || billingStateRequired || billingZipRequired) && (
        <div className="ach-form-row">
          {billingCityRequired && (
            <div className="ach-address-info">
              <TextField
                variant="standard"
                sx={classes.formLabel}
                required
                className="input-cc-billing-city"
                id="billing-city"
                name="billingCity"
                label="Billing City"
                value={formik.values.billingCity}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                inputProps={{ maxLength: 36 }}
              />
              {(formik.errors.billingCity && formik.touched.billingCity) ? <div className="input-error">{formik.errors.billingCity}</div> : null}
            </div>
          )}
          {billingStateRequired && (
            <FormControl variant="standard" sx={classes.formLabel} className="ach-state">
              <InputLabel
                required
                id="billing-state-select-label"
              >
                State
              </InputLabel>
              <Select
                labelId="billing-state-label-select-label"
                id="billing-state"
                name="billingState"
                value={formik.values.billingState}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                MenuProps={{ PaperProps: { sx: classes.menuPaper } }}
              >
                {formConstants.states.map((state) => (
                  <MenuItem key={state} value={state}>
                    {state}
                  </MenuItem>
                ))}
              </Select>
              {(formik.errors.billingState && formik.touched.billingState) ? <div className="input-error">{formik.errors.billingState}</div> : null}
            </FormControl>
          )}
          {billingZipRequired && (
            <div className="ach-address-info">
              <TextField
                variant="standard"
                sx={classes.formLabel}
                required
                id="billing-zip"
                name="billingZip"
                label="Billing Zip"
                value={formik.values.billingZip}
                onBlur={formik.handleBlur}
                onChange={(e) => {
                  const input = e.target.value.trim();
                  if (input !== '' && regex.alphaNumericSpaceDash.test(input) === false) {
                    return;
                  }
                  formik.handleChange(e);
                }}
                inputProps={{ maxLength: 12 }}
              />
              {(formik.errors.billingZip && formik.touched.billingZip) ? <div className="input-error">{formik.errors.billingZip}</div> : null}
            </div>
          )}
        </div>
      )}
      {driversLicenseRequired && (
        <div className="ach-form-row">
          <div className="ach-form-item-wrapper">
            <TextField
              variant="standard"
              sx={classes.formLabel}
              required
              className="ach-form-item"
              id="drivers-license-number"
              name="driversLicenseNumber"
              label="Drivers License Number"
              value={formik.values.driversLicenseNumber}
              onBlur={formik.handleBlur}
              onChange={(e) => formik.handleChange(e)}
              inputProps={{ maxLength: 50 }}
            />
            {(formik.errors.driversLicenseNumber && formik.touched.driversLicenseNumber) ? <div className="input-error">{formik.errors.driversLicenseNumber}</div> : null}
          </div>
          <FormControl variant="standard" sx={classes.formLabel} className="ach-form-item-wrapper">
            <InputLabel required id="drivers-license-state-label-select-label">Drivers License State</InputLabel>
            <Select
              labelId="drivers-license-state-label-select-label"
              id="drivers-license-state"
              name="driversLicenseState"
              className="ach-form-item"
              value={formik.values.driversLicenseState}
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              MenuProps={{ PaperProps: { sx: classes.menuPaper } }}
            >
              {formConstants.states.map((state) => (
                <MenuItem key={state} value={state}>
                  {state}
                </MenuItem>
              ))}
            </Select>
            {(formik.errors.driversLicenseState && formik.touched.driversLicenseState) ? <div className="input-error">{formik.errors.driversLicenseState}</div> : null}
          </FormControl>
        </div>
      )}
      {identityVerificationRequired && (
        <>
          <div className="identity-label">Enter one of the following</div>
          <div className="ach-form-row">
            <div className="ach-form-item-wrapper">
              <TextField
                variant="standard"
                sx={classes.formLabel}
                className="ach-form-item"
                id="ssn4"
                name="ssn4"
                label="Last 4 of SSN"
                value={formik.values.ssn4}
                onBlur={formik.handleBlur}
                onChange={(e) => {
                  // eslint-disable-next-line no-restricted-globals
                  if (e.target.value.trim() !== '' && isNaN(e.target.value)) {
                    return;
                  }
                  formik.handleChange(e);
                }}
                inputProps={{ maxLength: 4 }}
              />
              {(formik.errors.ssn4 && formik.touched.ssn4) ? <div className="input-error">{formik.errors.ssn4}</div> : null}
            </div>
            <div className="ach-form-item-wrapper">
              <TextField
                variant="standard"
                sx={classes.formLabel}
                className="ach-form-item"
                id="dob-year"
                name="dobYear"
                label="Year of Birth"
                value={formik.values.dobYear}
                onBlur={formik.handleBlur}
                onChange={(e) => {
                  // eslint-disable-next-line no-restricted-globals
                  if (e.target.value.trim() !== '' && isNaN(e.target.value)) {
                    return;
                  }
                  formik.handleChange(e);
                }}
                inputProps={{ maxLength: 4 }}
              />
              {(formik.errors.dobYear && formik.touched.dobYear) ? <div className="input-error">{formik.errors.dobYear}</div> : null}
            </div>
          </div>
        </>
      )}
      <PaymentInput
        agreement={paymentAgreement}
        submitting={submitting}
        acceptedAgreement={acceptedAgreement}
        allowPartialPayment={allowPartialPayment}
        allowOverPayment={allowOverPayment}
        remainingBalance={remainingBalance}
        paymentAmount={formik.values.paymentAmount}
        paymentAmountError={formik.errors.paymentAmount}
        paymentAmountTouched={formik.touched.paymentAmount}
        invoiceID={invoiceID}
        toggleAcceptedAgreement={() => toggleAcceptedAgreement(!acceptedAgreement)}
        handleChange={(e) => {
          formik.handleChange(e);
          setCurrentPaymentAmount(e.target.value);
        }}
        handleBlur={(e) => formik.handleBlur(e)}
      />
    </form>
  );
};

ACHPayment.propTypes = {
  invoiceID: PropTypes.string.isRequired,
  currentPaymentAmount: PropTypes.string.isRequired,
  setCurrentPaymentAmount: PropTypes.func.isRequired,
};

export default ACHPayment;
